import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, Clock, Search, Filter, X, Tag,
  ArrowRight, ArrowLeft, Grid, List
} from 'lucide-react';
import { ArticleService } from './apiService';
import SiteHeader from './components/SiteHeader';
import { translations } from './translations';
import './News.css';

const News = () => {
  const lang = localStorage.getItem('lang') || 'en';
  const t = (translations[lang] || translations.en).newsPage;
  const locale = lang === 'fr' ? 'fr-FR' : 'en-US';

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTags, setSelectedTags] = useState([]);
  const [sortBy, setSortBy] = useState('date');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showFilters, setShowFilters] = useState(false);

  // Catégories disponibles
  const categories = useMemo(() => {
    const cats = ['All', ...new Set(articles.map(article => article.category?.name || article.category).filter(Boolean))];
    return cats;
  }, [articles]);

  // Tags disponibles
  const availableTags = useMemo(() => {
    const tags = new Set();
    articles.forEach(article => {
      if (article.tags && Array.isArray(article.tags)) {
        article.tags.forEach(tag => {
          const tagName = tag.name || tag;
          if (tagName) tags.add(tagName);
        });
      }
    });
    return Array.from(tags).sort();
  }, [articles]);

  // Charger les articles depuis l'API
  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const response = await ArticleService.getAll({
          status: 'published',
          sort_by: 'publish_date',
          sort_order: 'desc',
          per_page: 100
        });

        if (response.success) {
          const mappedArticles = response.data.data.map(article => ({
            ...article,
            date: new Date(article.publish_date || article.date).toLocaleDateString(locale, {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }),
            dateTimestamp: new Date(article.publish_date || article.date).getTime(),
            readTime: article.read_time || '5 min read',
            image: article.imageArticle || article.image,
            category: article.category?.name || article.category,
            tags: article.tags ? article.tags.map(tag => tag.name || tag) : [],
            author: {
              name: article.author?.name || 'Unknown Author',
              avatar: article.author?.avatar || '../12.png'
            },
            featured: false
          }));

          setArticles(mappedArticles);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [locale]);

  // Filtrer et trier les articles
  const filteredArticles = useMemo(() => {
    let filtered = [...articles];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(query) ||
        article.excerpt.toLowerCase().includes(query) ||
        article.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(article => article.category === selectedCategory);
    }

    if (selectedTags.length > 0) {
      filtered = filtered.filter(article =>
        selectedTags.every(tag => article.tags.includes(tag))
      );
    }

    switch (sortBy) {
      case 'date':
        filtered.sort((a, b) => b.dateTimestamp - a.dateTimestamp);
        break;
      case 'title':
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
      default:
        break;
    }

    return filtered;
  }, [articles, searchQuery, selectedCategory, selectedTags, sortBy]);

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t2 => t2 !== tag)
        : [...prev, tag]
    );
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All');
    setSelectedTags([]);
    setSortBy('date');
  };

  const hasActiveFilters = searchQuery || selectedCategory !== 'All' || selectedTags.length > 0;

  return (
    <div className="news-page">
      <SiteHeader />


      {/* Main Content with Filters */}
      <section className="news-main">
        <div className="container">
          {/* Search and Filter Bar */}
          <div className="news-controls">
            <div className="news-search-wrapper">
              <Search size={20} className="search-icon" />
              <input
                type="text"
                placeholder={t.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="news-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="search-clear"
                  aria-label="Clear search"
                >
                  <X size={18} />
                </button>
              )}
            </div>

            <div className="news-controls-right">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
              >
                <Filter size={20} />
                {t.filters}
                {hasActiveFilters && <span className="filter-badge"></span>}
              </button>

              <div className="view-mode-toggle">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  aria-label="Grid view"
                >
                  <Grid size={20} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
                  aria-label="List view"
                >
                  <List size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="filter-panel">
              <div className="filter-panel-header">
                <h3>{t.filters}</h3>
                {hasActiveFilters && (
                  <button onClick={clearAllFilters} className="clear-filters-btn">
                    <X size={16} />
                    {t.clearAll}
                  </button>
                )}
              </div>

              <div className="filter-groups">
                <div className="filter-group">
                  <h4>{t.category}</h4>
                  <div className="filter-options">
                    {categories.map(category => (
                      <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={`filter-option ${selectedCategory === category ? 'active' : ''}`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="filter-group">
                  <h4>{t.tags}</h4>
                  <div className="filter-options tags-grid">
                    {availableTags.map(tag => (
                      <button
                        key={tag}
                        onClick={() => toggleTag(tag)}
                        className={`filter-option tag-option ${selectedTags.includes(tag) ? 'active' : ''}`}
                      >
                        <Tag size={14} />
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="filter-group">
                  <h4>{t.sortBy}</h4>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="sort-select"
                  >
                    <option value="date">{t.newestFirst}</option>
                    <option value="title">{t.alphabetical}</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="active-filters">
              <span className="active-filters-label">{t.activeFilters}</span>
              {selectedCategory !== 'All' && (
                <span className="active-filter-tag">
                  {t.categoryPrefix} {selectedCategory}
                  <button onClick={() => setSelectedCategory('All')}>
                    <X size={14} />
                  </button>
                </span>
              )}
              {selectedTags.map(tag => (
                <span key={tag} className="active-filter-tag">
                  {tag}
                  <button onClick={() => toggleTag(tag)}>
                    <X size={14} />
                  </button>
                </span>
              ))}
              {searchQuery && (
                <span className="active-filter-tag">
                  {t.searchPrefix} "{searchQuery}"
                  <button onClick={() => setSearchQuery('')}>
                    <X size={14} />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Results Count */}
          <div className="results-info">
            <p>
              {t.showing} <strong>{filteredArticles.length}</strong> {t.of} <strong>{articles.length}</strong> {t.articlesLabel}
            </p>
          </div>

          {/* Articles Grid/List */}
          {loading ? (
            <div className="news-loading">
              <div className="loading-spinner"></div>
              <p>{t.loadingArticles}</p>
            </div>
          ) : filteredArticles.length > 0 ? (
            <div className={`articles-container ${viewMode}`}>
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/news/${article.id}`}
                  className="article-card"
                >
                  <div className="article-card-image">
                    <img src={article.image} alt={article.title} />
                    <span className="article-card-category">{article.category}</span>
                  </div>
                  <div className="article-card-content">
                    <h3 className="article-card-title">{article.title}</h3>
                    <p className="article-card-excerpt">{article.excerpt}</p>

                    <div className="article-card-tags">
                      {article.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="article-tag">
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="article-card-footer">
                      {/* Auteur masqué */}
                      <div className="article-card-meta">
                        <span><Calendar size={14} /> {article.date}</span>
                        <span><Clock size={14} /> {article.readTime}</span>
                      </div>
                    </div>

                    <div className="article-card-read-more">
                      {t.readMore} <ArrowRight size={16} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="no-results">
              <Search size={48} />
              <h3>{t.noArticlesFound}</h3>
              <p>{t.tryAdjusting}</p>
              {hasActiveFilters && (
                <button onClick={clearAllFilters} className="clear-filters-btn-large">
                  {t.clearAllFilters}
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Return to Website Section */}
      <section className="return-website-section">
        <div className="container">
          <div className="return-website-content">
            <h2>{t.exploreMore}</h2>
            <p>{t.discoverMore}</p>
            <Link to="/" className="return-website-btn">
              <ArrowLeft size={20} />
              {t.returnToWebsite}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default News;
